import { showDialog } from '@/shared/sweetAlerts/showDialog'
import DicomPlusMixin from '@/shared/mixins/dicom-plus.mixin'
import DicomPlusSoc from '@/components/dicom-plus/dicom-plus-soc/index.vue'
import { aplicarMascaraCpf, validarCpf } from '@/shared/utils/cpf-validator'
import { canShowExamSoc, initialSocState } from '@/shared/utils/exam-soc.helper'

export default {
  name: 'exam-soc-link',
  components: { DicomPlusSoc },
  mixins: [DicomPlusMixin],
  props: {
    details: { type: Object, required: true }
  },
  data() {
    return {
      state: initialSocState(this.details),
      // Holter pode chegar sem CPF; o usuário informa o CPF para consultar a ficha.
      cpf: aplicarMascaraCpf(String(this.details.patient?.cpf || '')),
      allFiles: false,
      searching: false,
      saving: false,
      showModal: false,
      items: [],
      total: 0,
      page: 1,
      pageSize: 10,
      pageCount: 0,
      message: '',
      error: false,
      searchParams: null,
      disposed: false
    }
  },
  computed: {
    visible() {
      return canShowExamSoc(this.details)
    },
    busy() {
      return this.searching || this.saving
    },
    editable() {
      return this.visible && this.state.portalSocId == null
    },
    sourceAvailable() {
      return Number(this.details.sourceId) > 0
    },
    cpfValid() {
      return validarCpf(this.cpf)
    },
    lockMessage() {
      if (this.state.portalSocId != null) return this.$t('exam_soc.locked')
      return this.$t('exam_soc.unavailable')
    }
  },
  beforeDestroy() {
    this.disposed = true
  },
  methods: {
    showSearchWarning(message) {
      showDialog({
        icon: 'warning',
        title: this.$t('dicom_plus.no_data_found'),
        text: message || this.$t('dicom_plus.soc.no_files_for_CPF'),
        confirmButtonText: 'OK'
      })
    },
    confirmSocLink() {
      return new Promise((resolve) => {
        showDialog({
          icon: 'question',
          title: this.$t('exam_soc.confirm_title'),
          text: this.$t('exam_soc.confirm_text'),
          confirmButtonText: this.$t('yes'),
          denyButtonText: this.$t('no'),
          confirmed: () => resolve(true),
          denied: () => resolve(false)
        })
      })
    },
    formatCpf(event) {
      const formatted = aplicarMascaraCpf(event.target.value)
      // Remove do campo os caracteres não permitidos.
      event.target.value = formatted
      this.cpf = formatted
    },
    async search(page = 1, pageSize = this.pageSize) {
      if (!this.editable || !this.sourceAvailable || this.busy || !this.cpfValid) return
      if (!this.showModal) {
        this.searchParams = {
          cpf: this.cpf.replace(/\D/g, ''),
          fonteID: this.details.sourceId,
          patientName: this.details.patient?.name,
          allFiles: this.allFiles
        }
      }
      this.searching = true
      this.message = ''
      this.error = false
      try {
        const result = await this.getSocFiles({ ...this.searchParams, page, pageSize })
        if (this.disposed) return
        this.items = result.items
        this.total = result.total
        this.pageCount = result.pageCount
        this.page = page
        this.pageSize = pageSize
        if (result.total === 0) {
          this.showModal = false
          this.showSearchWarning()
        } else if (result.total === 1 && result.items.length === 1 && !this.showModal) {
          await this.save(result.items[0].portalSocId ?? result.items[0].portalSocID)
        } else {
          this.showModal = true
        }
      } catch (error) {
        if (!this.disposed) {
          this.showModal = false
          this.showSearchWarning(error?.response?.data?.errors)
        }
      } finally {
        this.searching = false
      }
    },
    async selectRecords(items) {
      if (this.busy || items.length !== 1) return
      await this.save(items[0].portalSocId ?? items[0].portalSocID)
    },
    async save(portalSocId) {
      if (!this.editable || !this.sourceAvailable || this.saving) return
      const modalWasOpen = this.showModal
      this.showModal = false
      const confirmed = await this.confirmSocLink()
      if (!confirmed || this.disposed) {
        if (modalWasOpen && !this.disposed) this.showModal = true
        return
      }
      this.saving = true
      this.message = ''
      this.error = false
      try {
        await this.linkSocFile(this.details.examId, portalSocId)
        if (this.disposed) return
        this.state = {
          portalSocId
        }
        this.$root.$emit('toast', { message: this.$t('exam_soc.success'), type: 'success' })
        // Atualiza na tela a ficha salva no backend.
        this.$set(this.details, 'portalSocId', portalSocId)
        this.$emit('linked', this.state)
      } catch (error) {
        if (this.disposed) return
        this.error = true
        this.message = this.$t('exam_soc.save_error')
      } finally {
        this.saving = false
      }
    }
  }
}
